import type { ReactNode } from "react";
import { Link } from "react-router";
import {
  Badge,
  ButtonLink,
  Card,
  EmptyState,
  PageHeader,
  Quantity,
  RelativeTime,
  Skeleton,
  Stat,
  StatGroup,
  type BadgeTone,
} from "@/components";
import { useBatches, useReminders, type ReminderKind } from "@/features/bench/api";
import { useFormulas } from "@/features/formulas/api";
import { useLowStock } from "@/features/inventory/api";
import { useAlerts, useUnreadAlertCount } from "@/features/purchasing/api";
import { Decimal } from "@/lib/decimal";
import styles from "./DashboardPage.module.css";

const REMINDER_LOOK: Record<ReminderKind, { label: string; tone: BadgeTone }> = {
  resmell: { label: "Re-smell", tone: "info" },
  shelf_life: { label: "Shelf life", tone: "warning" },
  retest: { label: "Retest", tone: "warning" },
  reorder: { label: "Reorder", tone: "neutral" },
  custom: { label: "Reminder", tone: "neutral" },
};

/** Home: what needs attention today across the bench, stock and purchasing. */
export function DashboardPage() {
  const reminders = useReminders({ status: "due", limit: 8 });
  const lowStock = useLowStock();
  const batches = useBatches({ status: "active", limit: 6 });
  const alerts = useAlerts({ unread: true, limit: 6 });
  const unread = useUnreadAlertCount();
  const formulas = useFormulas({ sort: "-updated", limit: 6 });

  const lowCount = lowStock.data ? lowStock.data.materials.length + lowStock.data.lots.length : undefined;

  return (
    <div className={styles.page}>
      <PageHeader
        title="Today"
        description="What needs attention across the bench, stock and purchasing."
        actions={
          <>
            <ButtonLink to="/inventory/receive" icon="truck">
              Receive stock
            </ButtonLink>
            <ButtonLink to="/formulas" icon="flask">
              Formulas
            </ButtonLink>
            <ButtonLink to="/bench/new" variant="primary" icon="scale">
              Start a trial
            </ButtonLink>
          </>
        }
      />

      <StatGroup className={styles.stats}>
        <Stat label="Reminders due" value={reminders.data?.count ?? "—"} />
        <Stat
          label="Running low"
          value={lowCount ?? "—"}
          hint="Materials below their reorder point and nearly empty lots"
        />
        <Stat label="On the bench" value={batches.data?.count ?? "—"} hint="Batches planned or being weighed" />
        <Stat label="Unread alerts" value={unread.data?.count ?? "—"} />
      </StatGroup>

      <div className={styles.grid}>
        <Card
          title="Due now"
          actions={
            <Link to="/reminders" className={styles.more}>
              All reminders
            </Link>
          }
        >
          <Section
            loading={reminders.isPending}
            empty={!reminders.data?.items.length}
            emptyTitle="Nothing due"
            emptyText="Re-smell, shelf-life and retest reminders appear here when they fall due."
          >
            <ul className={styles.list}>
              {reminders.data?.items.map((reminder) => {
                const look = REMINDER_LOOK[reminder.kind];
                const title = reminder.subject_url ? (
                  <Link to={reminder.subject_url} className={styles.itemLink}>
                    {reminder.title}
                  </Link>
                ) : (
                  <span className={styles.itemTitle}>{reminder.title}</span>
                );
                return (
                  <li key={reminder.id} className={styles.item}>
                    <Badge tone={look.tone}>{look.label}</Badge>
                    {title}
                    <RelativeTime value={reminder.due_at} className={styles.meta} />
                  </li>
                );
              })}
            </ul>
          </Section>
        </Card>

        <Card
          title="Running low"
          actions={
            <Link to="/inventory?tab=low" className={styles.more}>
              Inventory
            </Link>
          }
        >
          <Section
            loading={lowStock.isPending}
            empty={!lowCount}
            emptyTitle="Stock looks fine"
            emptyText="Set reorder points on materials to be warned before a bottle runs out."
          >
            <ul className={styles.list}>
              {lowStock.data?.materials.map((row) => {
                const ordered = Decimal.tryFrom(row.on_order_g)?.isZero() === false;
                return (
                  <li key={`m-${row.material.id}`} className={styles.item}>
                    {ordered ? <Badge tone="info">On order</Badge> : <Badge tone="warning">Reorder</Badge>}
                    <Link to={`/materials/${row.material.id}`} className={styles.itemLink}>
                      {row.material.name}
                    </Link>
                    <span className={styles.meta}>
                      <Quantity value={row.balance_g} unit="g" /> of <Quantity value={row.min_g} unit="g" />
                      {ordered ? (
                        <>
                          , <Quantity value={row.on_order_g} unit="g" /> on order
                        </>
                      ) : null}
                    </span>
                  </li>
                );
              })}
              {lowStock.data?.lots.map((lot) => (
                <li key={`l-${lot.id}`} className={styles.item}>
                  <span className={styles.code}>{lot.display_code}</span>
                  <Link to={`/lots/${lot.code}`} className={styles.itemLink}>
                    {lot.material.name}
                    {lot.dilution_label ? ` (${lot.dilution_label})` : ""}
                  </Link>
                  <span className={styles.meta}>
                    <Quantity value={lot.balance_g} unit="g" /> left
                  </span>
                </li>
              ))}
            </ul>
          </Section>
        </Card>

        <Card
          title="On the bench"
          actions={
            <Link to="/bench" className={styles.more}>
              Bench
            </Link>
          }
        >
          <Section
            loading={batches.isPending}
            empty={!batches.data?.items.length}
            emptyTitle="No trials in progress"
            emptyText="Plan a trial from a formula version to weigh it with your balance."
          >
            <ul className={styles.list}>
              {batches.data?.items.map((batch) => (
                <li key={batch.id} className={styles.item}>
                  <span className={styles.code}>{batch.code}</span>
                  <Link to={`/bench/${batch.id}`} className={styles.itemLink}>
                    {batch.title}
                  </Link>
                  <span className={styles.meta}>
                    {batch.lines_done}/{batch.lines_total} lines
                  </span>
                </li>
              ))}
            </ul>
          </Section>
        </Card>

        <Card
          title="Alerts"
          actions={
            <Link to="/alerts" className={styles.more}>
              All alerts
            </Link>
          }
        >
          <Section
            loading={alerts.isPending}
            empty={!alerts.data?.items.length}
            emptyTitle="No new alerts"
            emptyText="Watch a material or listing to hear about price drops and restocks."
          >
            <ul className={styles.list}>
              {alerts.data?.items.map((alert) => (
                <li key={alert.id} className={styles.item}>
                  <span className={styles.itemTitle}>{alert.title}</span>
                  <span className={styles.detail}>{alert.detail}</span>
                  <RelativeTime value={alert.created_at} className={styles.meta} />
                </li>
              ))}
            </ul>
          </Section>
        </Card>

        <Card
          title="Recent formulas"
          actions={
            <Link to="/formulas" className={styles.more}>
              All formulas
            </Link>
          }
          className={styles.wide}
        >
          <Section
            loading={formulas.isPending}
            empty={!formulas.data?.items.length}
            emptyTitle="No formulas yet"
            emptyText="Create a formula, paste one from Reddit or a book, or import from Formulair or Molequles."
          >
            <ul className={styles.list}>
              {formulas.data?.items.map((formula) => (
                <li key={formula.id} className={styles.item}>
                  <span className={styles.code}>{formula.code}</span>
                  <Link to={`/formulas/${formula.id}`} className={styles.itemLink}>
                    {formula.name}
                  </Link>
                  <span className={styles.meta}>
                    {formula.latest_released_label ?? "draft"} · <RelativeTime value={formula.updated_at} />
                  </span>
                </li>
              ))}
            </ul>
          </Section>
        </Card>
      </div>
    </div>
  );
}

function Section({
  loading,
  empty,
  emptyTitle,
  emptyText,
  children,
}: {
  loading: boolean;
  empty: boolean;
  emptyTitle: string;
  emptyText: string;
  children: ReactNode;
}) {
  if (loading) return <Skeleton lines={3} />;
  if (empty) return <EmptyState variant="inline" headingLevel={3} title={emptyTitle} description={emptyText} />;
  return <>{children}</>;
}
